/**
 * Picks the tenant dashboard: V2 behind the flag, V1 otherwise (COL-533).
 *
 * The choice is made here, at the route, rather than with an early return
 * inside `Dashboard.tsx`. That page runs dozens of hooks before it renders
 * anything; returning early from it would change how many hooks run between
 * renders the moment the flag or the escape hatch flipped, which breaks the
 * rules of hooks. Swapping whole components keeps each one's hook order
 * intact.
 *
 * `?dashboard=v1` forces the old page regardless of the flag. That is the
 * escape hatch V2 itself links to for anything not ported yet, and it means
 * no tenant can reach a dead end: whatever V2 cannot do, the old dashboard
 * still can, one click away.
 */

import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import TenantDashboardV2 from "@/components/tenant/v2/TenantDashboardV2";
import { useAuth } from "@/contexts/AuthContext";
import { isTenantDashboardV2Enabled } from "@/lib/featureFlags";
import { FORCE_V1_PARAM, FORCE_V1_VALUE } from "@/lib/tenant-dashboard-tabs";
import Dashboard from "@/pages/Dashboard";

export { FORCE_V1_PARAM, FORCE_V1_VALUE };

/*
  There is no longer a V1-only-return latch.

  External services (DocuSign, Stripe) redirect the tenant back mid-flow, and
  for a while the route had to recognise those returns and force them to V1,
  because V1 was the only surface that reconciled them: a tenant who signed or
  paid from V2 would otherwise land on the old dashboard. One by one V2 grew
  its own owners for each hop -- `settings` (AccountSettingsDialog),
  `docusign`/`event` (`useDocusignSigningReturn`), and now `payment`/
  `session_id` (`useStripePaymentReturn`, which calls
  `confirm-payment-from-session` exactly as V1 did) -- so the last param left
  the list and the latch with it. V1 keeps its own handlers; it is still
  reachable directly via `?dashboard=v1`.
*/

export default function DashboardRoute() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    isOwnerPortalOnly,
    isDemoDualViewAccount,
    isDemoDualViewReady,
    loading: authLoading,
  } = useAuth();
  const forcedV1 = searchParams.get(FORCE_V1_PARAM) === FORCE_V1_VALUE;

  /**
   * Owner-portal-only accounts belong in /admin, not on a tenant dashboard.
   *
   * This lived in `Dashboard.tsx` alone, so with the flag on V2 rendered
   * instead and the redirect never ran — an owner landed in the applicant
   * view. It has to sit at the route, above the V1/V2 choice, because it is
   * about WHO the account is rather than which dashboard they get.
   *
   * The demo account (COL-447) is deliberately exempt: it owns the hidden
   * demo property AND holds a booking in it, so the tenant dashboard is a
   * real view of its own data. Waiting on `isDemoDualViewReady` as well as
   * the roles matters — roles resolve first, and deciding on them alone
   * bounces the demo account off its own dashboard on a cold load.
   *
   * V1 keeps its own copy: it is still reachable directly via
   * `?dashboard=v1`, and a guard that only one of two entry points enforces
   * is not a guard.
   */
  useEffect(() => {
    if (
      !authLoading &&
      isDemoDualViewReady &&
      isOwnerPortalOnly &&
      !isDemoDualViewAccount
    ) {
      navigate("/admin?tab=analytics", { replace: true });
    }
  }, [
    authLoading,
    isOwnerPortalOnly,
    isDemoDualViewAccount,
    isDemoDualViewReady,
    navigate,
  ]);

  if (isTenantDashboardV2Enabled() && !forcedV1) {
    return <TenantDashboardV2 />;
  }
  return <Dashboard />;
}
